import {
  AddressInputModel,
  BusinessDetails,
  CreateBusinessInputModel,
  MCCCodeModel,
  MerchantAccount,
  MerchantAccountDetails,
  MerchantApplicationInputModel,
  PrimaryContactDetails,
} from "../../shared/models";
import {
  AuthService,
  IAPICommonResponse,
  IAPIResponse,
  IOption,
  PaginationModel,
  ToastService,
} from "@mindmemobile/mm-shared-global";
import { action, makeAutoObservable, observable, when } from "mobx";
import { firstValueFrom } from "rxjs";
import { CountryStore } from "../../shared/store";
import { MerchantAccountService } from "../../shared/services";
import { ApplicationStatus } from "../../shared/enums";
import { IAPIUpdateApplicationStateModel } from "../../shared/interfaces";
import {
  NMIMerchantAccountModel,
  NMIProcessorFieldsModel,
} from "../../shared/models/NMIFormModels";

export default class MerchantAccountStore {
  @observable applicationData: MerchantApplicationInputModel;
  @observable nmiApplicationData: NMIMerchantAccountModel;
  @observable businessData: CreateBusinessInputModel;
  @observable isNewBusiness: boolean;
  @observable businesses: BusinessDetails[];
  @observable completeApplicationLink: string;
  @observable merchantAccounts: MerchantAccount[];
  @observable selectedMerchantAccountId: string;
  @observable mccCodes: MCCCodeModel[];
  @observable accountName: string;
  public pagination: PaginationModel = new PaginationModel({
    pageNumber: 1,
    pageSize: 999999,
  });
  @observable cardLink: string;
  //countryStore: CountryStore;
  @observable isSaveInProgress: boolean;
  @observable isBusinessSubmission: boolean;
  public isMerchantAccountsLoadingInProgress: boolean = true;

  constructor() {
    makeAutoObservable(this);
    this.applicationData = new MerchantApplicationInputModel();
    this.businessData = new CreateBusinessInputModel();
    this.isNewBusiness = false;
    this.businesses = [];
    this.completeApplicationLink = "";
    this.merchantAccounts = [];
    this.selectedMerchantAccountId = "";
    this.mccCodes = [];
    this.cardLink = "";
    this.accountName = "";
    // this.countryStore = new CountryStore();
    this.isSaveInProgress = false;
    this.isBusinessSubmission = false;
    this.isMerchantAccountsLoadingInProgress = false;
    this.nmiApplicationData = new NMIMerchantAccountModel();

    // this.countryStore.onCountryDataLoaded.subscribe((countryData) => {
    //   if (countryData) {
    //     const usCountry = countryData.find((c) => c.abbreviationName === "US");
    //     if (usCountry) {
    //       this.applicationData = new MerchantApplicationInputModel({
    //         ...this.applicationData,
    //         address: new AddressInputModel({
    //           ...this.applicationData.address,
    //           countryDetails: usCountry,
    //         }),
    //         accountDetails: new MerchantAccountDetails({
    //           ...this.applicationData.accountDetails,
    //           countryDetails: usCountry,
    //         }),
    //       });

    //       this.businessData = new CreateBusinessInputModel({
    //         ...this.businessData,
    //         primaryContactDetails: new PrimaryContactDetails({
    //           ...this.businessData.primaryContactDetails,
    //           countryDetails: usCountry,
    //         }),
    //       });

    //       this.nmiApplicationData = new NMIMerchantAccountModel({
    //         ...this.nmiApplicationData,
    //         countryDetails: usCountry,
    //         phoneCountryDetails: usCountry,
    //         processorFields: new NMIProcessorFieldsModel({
    //           ...this.nmiApplicationData.processorFields,
    //           country: usCountry,
    //         }),
    //       });
    //     }
    //   }
    // });
  }

  initNewApplicationData(countryStore: CountryStore): void {
    if (countryStore) {
      const usCountry = countryStore.getUSCountry();
      if (usCountry) {
        this.applicationData = new MerchantApplicationInputModel({
          ...this.applicationData,
          address: new AddressInputModel({
            ...this.applicationData.address,
            countryDetails: usCountry,
          }),
          accountDetails: new MerchantAccountDetails({
            ...this.applicationData.accountDetails,
            countryDetails: usCountry,
          }),
        });

        this.businessData = new CreateBusinessInputModel({
          ...this.businessData,
          primaryContactDetails: new PrimaryContactDetails({
            ...this.businessData.primaryContactDetails,
            countryDetails: usCountry,
          }),
        });

        this.nmiApplicationData = new NMIMerchantAccountModel({
          ...this.nmiApplicationData,
          countryDetails: usCountry,
          phoneCountryDetails: usCountry,
          processorFields: new NMIProcessorFieldsModel({
            ...this.nmiApplicationData.processorFields,
            country: usCountry,
          }),
        });
      }
    }
  }

  resetNMIApplicationData(countryStore: CountryStore): void {
    const usCountry = countryStore.getUSCountry();
    this.nmiApplicationData = new NMIMerchantAccountModel({
      countryDetails: usCountry,
      phoneCountryDetails: usCountry,
    });
  }

  @action
  toggleBusinessState(): void {
    this.isNewBusiness = !this.isNewBusiness;
  }

  @action
  getMCCCodeDropdownOptions(): IOption[] {
    return this.mccCodes.map((mccCode) => ({
      label: `${mccCode.code} - ${mccCode.industry}`,
      value: mccCode.code,
    }));
  }

  @action
  updateApplicationData(data: MerchantApplicationInputModel): void {
    this.applicationData = new MerchantApplicationInputModel(data);
  }

  @action
  updateNmiApplicationData(data: NMIMerchantAccountModel): void {
    this.nmiApplicationData = new NMIMerchantAccountModel(data);
  }

  @action
  updateBusinessData(data: CreateBusinessInputModel): void {
    this.businessData = new CreateBusinessInputModel(data);
  }

  @action
  updateFormSubmissionState(state: boolean) {
    this.isSaveInProgress = state;
  }

  @action
  updateBusinessSubmissionState(state: boolean) {
    this.isBusinessSubmission = state;
  }

  @action
  validateDetails() {
    const isValid = this.applicationData.validate();
    this.applicationData = new MerchantApplicationInputModel({
      ...this.applicationData,
    });
    return isValid;
  }

  @action
  async submitNewBusiness(): Promise<void> {
    const isValid = this.businessData.validate();
    if (!isValid) {
      this.businessData = new CreateBusinessInputModel({
        ...this.businessData,
      });
      return;
    }
    this.updateBusinessSubmissionState(true);
    const apiModel = this.businessData.createBusinessAPIModel();
    const isValidName = await this.checkBusinessName(apiModel.name);

    if (isValidName) {
      const response =
        await MerchantAccountService.createBusiness(apiModel).toPromise();

      if (response?.result) {
        if (!AuthService.hasAccount && response?.result.accountId) {
          AuthService.setAuthData(
            response?.result.accountId,
            response?.result.shortUniqueId,
            response.result.isPrimarySubAccount,
            response.result.hasLibraryManagePermissions,
          );
        }
        await this.fetchBusinesses(apiModel.name);
        this.toggleBusinessState();
        this.setBusinessNameError(false);
        this.updateBusinessSubmissionState(false);
      }
    } else {
      this.updateBusinessSubmissionState(false);
    }
  }

  @action
  async checkBusinessName(businessName: string): Promise<boolean> {
    if (
      !AuthService.hasAccount ||
      AuthService.mindmeAccountDetails?.subAccountId
    ) {
      return true;
    }
    return await MerchantAccountService.getIsValidBusiness(
      AuthService.authData.accountId,
      businessName,
    )
      .toPromise()
      .then((response) => {
        if (response && !response.result) {
          return !response.result;
        } else {
          this.setBusinessNameError(true);
          return false;
        }
      });
  }

  @action
  setBusinessNameError(error: boolean): void {
    if (error) {
      this.updateBusinessData({
        ...this.businessData,
        businessNameError: "Business name already exists",
      } as CreateBusinessInputModel);
    } else {
      this.updateBusinessData({
        ...this.businessData,
        businessNameError: "",
      } as CreateBusinessInputModel);
    }
  }

  @action
  async fetchBusinesses(businessName?: string): Promise<BusinessDetails[]> {
    if (!AuthService.hasAccount) {
      return [];
    }
    const response = await MerchantAccountService.getBusinesses(
      AuthService.authData.accountId,
    ).toPromise();
    if (response?.result) {
      const businessData: BusinessDetails[] = response.result;
      this.setBusinesses(businessData);
      if (businessName) {
        const selectedBusiness = businessData.find(
          (item) => item.name === businessName,
        );
        this.updateSelectedBusiness(selectedBusiness);
      }
    }
    return response?.result;
  }

  @action
  async fetchMCCCodes(): Promise<MCCCodeModel[]> {
    // if (!AuthService.hasAccount) {
    //   return [];
    // }
    const response = await MerchantAccountService.getFiservMCCS().toPromise();
    if (response?.result) {
      this.mccCodes =
        response.result?.map((m) => MCCCodeModel.deserializeAPIModel(m)) || [];
    }
    return [];
  }

  @action
  updateSelectedBusiness(selectedBusiness: BusinessDetails | undefined): void {
    const updatedApplicationData = {
      ...this.applicationData,
      businessName: selectedBusiness?.name || "",
      businessId: selectedBusiness?.id || "",
    };
    this.updateApplicationData(
      new MerchantApplicationInputModel(updatedApplicationData),
    );
  }

  @action
  setBusinesses(businesses: BusinessDetails[]): void {
    this.businesses = businesses;
  }

  @action
  async submitApplication(): Promise<string> {
    const isValid = this.validateDetails();
    if (isValid) {
      try {
        const apiModel = this.applicationData.createAPIModel();
        const response =
          await MerchantAccountService.createMerchantAccount(
            apiModel,
          ).toPromise();
        if (response?.result.id) {
          this.fetchMerchantAccounts();
          this.completeApplicationLink =
            response?.result.completeApplicationLink;
          return response?.result.id;
        }
        return "";
      } catch (error) {
        console.error("Error submitting application", error);
        return "";
      }
    } else {
      return "";
    }
  }

  @action
  async submitNMIApplication(): Promise<string> {
    const username = this.nmiApplicationData.username;

    const isUsernameAvailable =
      await MerchantAccountService.IsNMIUserNameAvailable(
        AuthService.authData.accountId,
        username,
      ).toPromise();

    if (!isUsernameAvailable?.result) {
      ToastService.showError("Username already exists");
      return "";
    }

    const apiModel = this.nmiApplicationData.createAPIModel();
    const response =
      await MerchantAccountService.createMerchantAccount(apiModel).toPromise();

    if (response?.result.id) {
      if (!AuthService.hasAccount && response?.result.accountId) {
        AuthService.setAuthData(
          response?.result.accountId,
          response?.result.shortUniqueId,
          response?.result.isPrimarySubAccount,
          response?.result.hasLibraryManagePermissions,
        );
      }
      this.fetchMerchantAccounts();
      this.completeApplicationLink = response.result.completeApplicationLink;
      return response.result.id;
    }

    ToastService.showError("Error submitting NMI application");
    return "";
  }

  setMerchantAccountsData(merchantAccounts: MerchantAccount[]) {
    this.merchantAccounts = merchantAccounts;
  }

  @action
  setSelectedMerchantAccountId(merchantAccountId: string): void {
    this.selectedMerchantAccountId = merchantAccountId || "";
  }

  get selectedMerchantAccount(): MerchantAccount | undefined {
    if (!this.selectedMerchantAccountId) return undefined;

    return this.merchantAccounts.find(
      (m) => m.merchantAccountId === this.selectedMerchantAccountId,
    );
  }

  get activeMerchantAccounts(): MerchantAccount[] {
    return this.merchantAccounts.filter(
      (m) => m.applicationStatus === ApplicationStatus.Active,
    );
  }

  /**
   * Merchant account item builders copy their defaults from: the account
   * flagged as default, then the first active one.
   *
   * Deliberately ignores the account picked in the top bar. An item is not
   * scoped to a merchant account - it can be sold through any of them - so its
   * defaults must not change based on what the dashboard happened to be
   * filtered to when the user hit "create".
   */
  get itemDefaultsMerchantAccount(): MerchantAccount | undefined {
    const active = this.activeMerchantAccounts;
    return active.find((m) => m.isDefault) || active[0];
  }

  /**
   * Surcharge a newly created item (product, service, donation, membership)
   * starts from. An item only carries the on/off flag, so the account's rate
   * and "ask customer first" are not copied onto it.
   */
  get itemSurchargeDefault(): boolean {
    return (
      this.itemDefaultsMerchantAccount?.surchargeDefaults().surchargeEnabled ??
      false
    );
  }

  /**
   * Hands `apply` the item surcharge default once merchant accounts are known,
   * fetching them when nothing else has. Item builders mount before (or
   * alongside) the merchant account list, so the default is rarely readable
   * synchronously. Returns a disposer the caller runs on unmount.
   */
  whenItemSurchargeDefaultKnown(
    apply: (surcharge: boolean) => void,
  ): () => void {
    if (
      !this.merchantAccounts.length &&
      !this.isMerchantAccountsLoadingInProgress
    ) {
      this.fetchMerchantAccounts();
    }
    return when(
      () =>
        !this.isMerchantAccountsLoadingInProgress &&
        this.merchantAccounts.length > 0,
      () => apply(this.itemSurchargeDefault),
    );
  }

  /**
   * True when the account is running on the NMI gateway. Uses the merchant
   * account currently selected in the top bar and falls back to "every active
   * merchant account is NMI" when nothing has been selected yet (e.g. on a
   * deep link into a route that has no merchant account switcher).
   * Returns false while merchant accounts are still unknown, so Fiserv-only
   * features stay visible by default.
   */
  get isNMIGatewayAccount(): boolean {
    const selected = this.selectedMerchantAccount;
    if (selected) return selected.isNMIMerchantAccount();

    const active = this.activeMerchantAccounts;
    return active.length > 0 && active.every((m) => m.isNMIMerchantAccount());
  }

  @action
  async fetchMerchantAccounts() {
    if (!AuthService.hasAccount) {
      this.isMerchantAccountsLoadingInProgress = false;
      return;
    }

    this.isMerchantAccountsLoadingInProgress = true;

    try {
      const response = await MerchantAccountService.getMerchantAccounts(
        AuthService.authData.accountId,
        this.pagination.pageNumber,
        this.pagination.pageSize,
        "",
      ).toPromise();

      this.isMerchantAccountsLoadingInProgress = false;
      const mappedData =
        response?.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
        [];
      this.setMerchantAccountsData(mappedData);
    } catch (error) {
      this.isMerchantAccountsLoadingInProgress = false;
      console.error("Error fetching businesses", error);
    }
  }

  @action
  resetApplicationData(countryStore: CountryStore): void {
    this.applicationData = new MerchantApplicationInputModel({
      accountDetails: new MerchantAccountDetails({
        countryDetails: countryStore.getUSCountry(),
      }),
      address: new AddressInputModel({
        countryDetails: countryStore.getUSCountry(),
      }),
    });
  }

  @action
  resetBusinessData(countryStore: CountryStore): void {
    this.businessData = new CreateBusinessInputModel({
      primaryContactDetails: new PrimaryContactDetails({
        countryDetails: countryStore.getUSCountry(),
      }),
    });
  }

  @action
  fetchCompleteApplicationLink(id: string) {
    MerchantAccountService.getCompleteApplicationLink(
      AuthService.authData.accountId,
      id,
    ).subscribe(
      (response) => {
        this.cardLink = response.result;
        if (this.cardLink) {
          window.open(this.cardLink, "_blank");
        }
      },
      (error) => {
        console.error("Error fetching businesses", error);
      },
    );
    return this.businesses;
  }

  @action
  async updateApplicationState(
    id: string,
    status: keyof typeof ApplicationStatus,
  ): Promise<IAPIResponse<IAPICommonResponse>> {
    const requestData: IAPIUpdateApplicationStateModel = {
      accountId: AuthService.authData.accountId,
      id: id,
      status: status,
    };

    const response = await firstValueFrom(
      MerchantAccountService.updateApplicationStatus(requestData),
    );
    // if (response.result) {
    //   ToastService.showSuccess("Application status updated successfully");
    // } else {
    //   ToastService.showError("Error updating application status");
    // }
    // this.fetchMerchantAccounts();
    return response;
  }
  @action
  hasNoActiveAccounts(): boolean {
    return (
      this.merchantAccounts.length > 0 &&
      !this.merchantAccounts.some(
        (account) => account.applicationStatus === ApplicationStatus.Active,
      ) &&
      !this.isMerchantAccountsLoadingInProgress
    );
  }

  @action
  hasSinglePendingMerchantAccount(): boolean {
    return (
      this.merchantAccounts.length === 1 &&
      this.merchantAccounts[0].applicationStatus ===
        ApplicationStatus.Pending &&
      !this.isMerchantAccountsLoadingInProgress
    );
  }

  @action
  hasMultiplePendingMerchantAccounts(): boolean {
    return (
      this.merchantAccounts.filter(
        (m) => m.applicationStatus === ApplicationStatus.Pending,
      ).length > 1 && !this.isMerchantAccountsLoadingInProgress
    );
  }

  hasNotificationBar(): boolean {
    return (
      !AuthService.isMerchantAccountsRoute &&
      (this.hasNoActiveAccounts() || this.hasSinglePendingMerchantAccount())
    );
  }

  @action
  getPendingMerchantAccountId(): string {
    return (
      this.merchantAccounts.find(
        (m) => m.applicationStatus === ApplicationStatus.Pending,
      )?.id || ""
    );
  }

  @action
  async setDefaultMerchantAccount(merchantAccountId: string): Promise<void> {
    const response = await firstValueFrom(
      MerchantAccountService.setDefaultMerchantAccount(
        AuthService.authData.accountId,
        merchantAccountId,
      ),
    );
    if (response.result) {
      ToastService.showSuccess("Update Successful");
      this.fetchMerchantAccounts();
    } else {
      ToastService.showError("Update failed");
    }
  }
}

let merchantAccountStoreInstance: MerchantAccountStore | null = null;

/**
 * App-wide MerchantAccountStore. Lives here rather than in AppRoutes so that
 * shared components (e.g. the left sidebar) can read it without importing the
 * route tree.
 */
export const getMerchantAccountStore = (): MerchantAccountStore => {
  if (!merchantAccountStoreInstance) {
    merchantAccountStoreInstance = new MerchantAccountStore();
  }
  return merchantAccountStoreInstance;
};
